import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { useLang } from "../LanguageContext";

const Counter = ({ value, prefix = "", suffix = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const duration = 1800;
    const start = Date.now();
    let raf;
    const tick = () => {
      const p = Math.min(1, (Date.now() - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.floor(eased * value));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);

  return <span ref={ref} className="tabular-nums">{prefix}{n.toLocaleString()}{suffix}</span>;
};

export const Stats = () => {
  const { t } = useLang();
  return (
    <section data-testid="stats" className="relative py-20 md:py-28 bg-[#050505]">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8 }}
          data-testid="stats-tagline"
          className="font-display text-2xl md:text-4xl text-white/90 tracking-tight leading-snug max-w-4xl mb-16"
        >
          {t.stats.tagline.split(",").map((part, i, arr) => (
            <span key={i}>
              {i === arr.length - 1 ? <span className="text-gold-gradient italic">{part}</span> : `${part},`}
            </span>
          ))}
        </motion.p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-y-12 gap-x-6">
          {t.stats.items.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.1 }}
              data-testid={`stat-${i}`}
              className="text-center sm:text-left sm:border-l sm:border-white/10 sm:pl-6"
            >
              <div className="font-display text-4xl md:text-6xl text-gold-gradient">
                <Counter value={s.value} prefix={s.prefix} suffix={s.suffix} />
              </div>
              <div className="mt-3 text-xs md:text-sm tracking-wide text-[#a3a3a3] uppercase">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
